import React, { useState } from "react";
import { ArrowDownIcon } from "@heroicons/react/24/solid";
import {
  Card,
  CardHeader,
  Typography,
  Button,
  CardBody,
  Input,
} from "@material-tailwind/react";
import CreateEdlitPlatformClient from "../../../components/CreateEdlitPlatformClient";
import toast from "react-hot-toast";
import { createPlatformClient } from "../../../helperFunctions/apiFunction";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { useAuthUserToken } from "../../../hooks/useAuthUserToken";

const CreateNewClient = () => {
  const { refreshToken } = useAuthUserToken();
  const [errors, setErrors] = useState({});
  const [isLoadingButton, setIsLoadingButton] = useState(false);
  const navigate = useNavigate();

  const initialFormData = {
    clientLegalName: "",
    clientShortName: "",
    clientPrimaryContactFirstName: "",
    clientPrimaryContactMiddleName: "",
    clientPrimaryContactLastName: "",
    clientSecondaryContactFirstName: "",
    clientSecondContactMiddleName: "",
    clientSecondContactLastName: "",
    clientPrimaryContactEmail: "",
    clientSecondaryContactEmail: "",
    clientDomain: "",
    clientPrimaryContactPhoneNumber: "",
    clientSecondaryContactPhoneNumber: "",
    clientPrimaryContactMobileNumber: "",
    clientSecondaryContactMobileNumber: "",
    clientRegisteredAddress: "",
    clientCISO: "",
    clientLogo: "",
    clientCorrespondenceAddress: "",
    clientExpiryTimestamp: "",
  };
  const [formData, setFormData] = useState(initialFormData);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.clientLegalName.trim()) {
      newErrors.clientLegalName = "Legal Name is Required";
    }
    if (!formData.clientShortName.trim()) {
      newErrors.clientShortName = "Short Name is Required";
    }
    if (!formData.clientPrimaryContactFirstName.trim()) {
      newErrors.clientPrimaryContactFirstName = "First Name is Required";
    }
    if (!formData.clientPrimaryContactLastName.trim()) {
      newErrors.clientPrimaryContactLastName = "Last Name is Required";
    }
    if (!formData.clientPrimaryContactEmail.trim()) {
      newErrors.clientPrimaryContactEmail = "Email is Required";
    }
    if (!formData.clientPrimaryContactPhoneNumber.trim()) {
      newErrors.clientPrimaryContactPhoneNumber = "Phone Number is Required";
    }
    if (!formData.clientPrimaryContactMobileNumber.trim()) {
      newErrors.clientPrimaryContactMobileNumber = "Mobile Number is Required";
    }
    if (!formData.clientRegisteredAddress.trim()) {
      newErrors.clientRegisteredAddress = "Address is Required";
    }
    if (!formData.clientExpiryTimestamp.trim()) {
      newErrors.clientExpiryTimestamp = "Expiry Date is Required";
    }
    setErrors(newErrors);
  };

  const handleBlur = () => {
    validateForm();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoadingButton(true);
    validateForm();

    const primaryEmailDomain =
      formData?.clientPrimaryContactEmail.split("@")[1];
    const secondaryEmailDomain =
      formData?.clientSecondaryContactEmail.split("@")[1];

    const clientDomains = formData.clientDomain
      ? formData.clientDomain.split(",").map((domain) => domain.trim())
      : [];

    const dataToSubmit = {
      ...formData,
      clientExpiryTimestamp: formData.clientExpiryTimestamp
        ? moment(formData.clientExpiryTimestamp).toISOString()
        : "",
      clientDomain: clientDomains,
    };

    if (Object.keys(errors).length === 0) {
      const isPrimaryDomainValid = clientDomains.includes(primaryEmailDomain);
      const isSecondaryDomainValid = secondaryEmailDomain
        ? clientDomains.includes(secondaryEmailDomain)
        : true;
      if (isPrimaryDomainValid && isSecondaryDomainValid) {
        createPlatformClient(refreshToken, dataToSubmit)
          .then((response) => {
            if (response?.code === 200 || response?.code === 201) {
              toast.success(response.message, {
                className: "text-[12px]",
              });
            } else if (response?.code === 400 || response?.code === 401) {
              toast.error(response?.message, {
                className: "text-[12px]",
              });
            } else {
              toast.error(response?.message, {
                className: "text-[12px]",
              });
            }
          })
          .catch((error) => {
            toast.error("Something went wrong!", {
              className: "text-[12px]",
            });
            console.error("Error during login:", error);
          })
          .finally(() => {
            setFormData(initialFormData);
            setIsLoadingButton(false);
            navigate("/ClientManagement");
          });
      } else {
        // Provide more specific error messages based on which domain is invalid
        let errorMessage =
          "Both email domains must be included in the client domain list.";
        if (!isPrimaryDomainValid) {
          errorMessage =
            "Primary email domain is not included in the client domain list.";
        } else if (secondaryEmailDomain && !isSecondaryDomainValid) {
          errorMessage =
            "Secondary email domain is not included in the client domain list.";
        }
        toast.error(errorMessage);
        setIsLoadingButton(false);
      }
    }
  };

  return (
    <div className="app-main__inner !pl-20">
      <Card
        className="h-full w-full rounded-3xl shadow-xl"
        style={{ backgroundImage: `url('/assets/images/beams.jpg')` }}
      >
        <CardHeader
          floated={false}
          shadow={false}
          className="bg-transparent rounded-none !overflow-visible"
        >
          <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between md:gap-8">
            <div>
              <Typography variant="h5" color="blue-gray">
                Create New Client
              </Typography>
              <Typography color="gray" className="mt-1 font-normal">
                Add Client details
              </Typography>
            </div>
            <div className="flex flex-row md:flex-row items-center justify-between md:items-center gap-2 md:gap-8 mt-4 md:mt-0 cursor-not-allowed">
              <Button variant="outlined" size="sm" disabled={true}>
                <ArrowDownIcon strokeWidth={2} className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CreateEdlitPlatformClient
          formData={formData}
          setFormData={setFormData}
          handleBlur={handleBlur}
          errors={errors}
          setErrors={setErrors}
          // handleChange={handleChange}
          operation="create"
          handleSubmission={handleSubmit}
          isLoading={isLoadingButton}
        />
      </Card>
    </div>
  );
};

export default CreateNewClient;
